/**
 * Travel Map Admin API
 * Kết nối với CMS API: https://api-ansinhso.personal-huytd.io.vn
 */

import { request } from '@umijs/max';
import type {
  AdminPlace,
  AdminPlaceListParams,
  ApiListResponse,
  ApiResponse,
  CreatePlaceRequest,
  PlacePhoto,
  UpdatePlaceRequest,
} from './types';
import { parseApiResponse, transformCmsPlace, transformToCreateRequest } from './utils';

const API_BASE_URL = REACT_APP_BASE_URL;
const CMS_API_PATH = '/cms-api/tourist-places';

/** GET /cms-api/tourist-places */
export async function getPlaces(params?: AdminPlaceListParams): Promise<ApiListResponse<AdminPlace>> {
  try {
    const response = await request(`${API_BASE_URL}${CMS_API_PATH}`, {
      method: 'GET',
      params: { category: params?.category, search: params?.search },
    });

    const { data, isSuccess, message } = parseApiResponse<any[]>(response);
    const dataArray = data || [];
    const places = dataArray.map(transformCmsPlace);

    let filteredPlaces = places;
    if (params?.status) {
      filteredPlaces = places.filter((p) => p.status === params.status);
    }

    const page = params?.page || 1;
    const limit = params?.limit || 10;
    const startIndex = (page - 1) * limit;
    const paginatedPlaces = filteredPlaces.slice(startIndex, startIndex + limit);

    return {
      success: isSuccess,
      message,
      data: paginatedPlaces,
      pagination: {
        currentPage: page,
        totalPages: Math.ceil(filteredPlaces.length / limit),
        totalItems: filteredPlaces.length,
        itemsPerPage: limit,
      },
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Lỗi khi tải danh sách địa điểm',
      data: [],
      pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 },
    };
  }
}

/** GET /cms-api/tourist-places/{id} */
export async function getPlaceDetail(id: string): Promise<ApiResponse<AdminPlace | null>> {
  try {
    const response = await request(`${API_BASE_URL}${CMS_API_PATH}/${id}`, { method: 'GET' });
    const { data, isSuccess, message } = parseApiResponse<any>(response);
    return {
      success: isSuccess && !!data?.id,
      message,
      data: data?.id ? transformCmsPlace(data) : null,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Không tìm thấy địa điểm',
      data: null,
    };
  }
}

/** POST /cms-api/tourist-places */
export async function createPlace(data: CreatePlaceRequest): Promise<ApiResponse<AdminPlace | null>> {
  try {
    const response = await request(`${API_BASE_URL}${CMS_API_PATH}`, {
      method: 'POST',
      data: transformToCreateRequest(data),
    });
    const parsed = parseApiResponse<any>(response);
    const apiData = parsed.data || response;
    return {
      success: parsed.isSuccess,
      message: parsed.message,
      data: apiData?.id ? transformCmsPlace(apiData) : null,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Tạo địa điểm thất bại',
      data: null,
    };
  }
}

/** PUT /cms-api/tourist-places/{id} */
export async function updatePlace(id: string, data: UpdatePlaceRequest): Promise<ApiResponse<AdminPlace | null>> {
  try {
    const response = await request(`${API_BASE_URL}${CMS_API_PATH}/${id}`, {
      method: 'PUT',
      data: transformToCreateRequest(data),
    });
    const parsed = parseApiResponse<any>(response);
    const apiData = parsed.data || response;
    return {
      success: parsed.isSuccess,
      message: parsed.message,
      data: apiData?.id ? transformCmsPlace(apiData) : null,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Cập nhật địa điểm thất bại',
      data: null,
    };
  }
}

/** DELETE /cms-api/tourist-places/{id} */
export async function deletePlace(id: string): Promise<ApiResponse<{ id: string } | null>> {
  try {
    const response = await request(`${API_BASE_URL}${CMS_API_PATH}/${id}`, { method: 'DELETE' });
    const { isSuccess, message } = parseApiResponse<any>(response);
    return {
      success: isSuccess,
      message,
      data: isSuccess ? { id } : null,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Xóa địa điểm thất bại',
      data: null,
    };
  }
}

/** POST /cms-api/file/upload */
export async function uploadPhoto(
  id: string,
  file: File,
): Promise<ApiResponse<{ placeId: string; photo: PlacePhoto } | null>> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response: any = await request(`${API_BASE_URL}/cms-api/file/upload`, {
      method: 'POST',
      data: formData,
      requestType: 'form',
    });

    const photoUrl = response?.data ?? response;
    if (!photoUrl) {
      return { success: false, message: 'Upload failed', data: null };
    }

    const photo: PlacePhoto = {
      id: `photo-${Date.now()}`,
      url: typeof photoUrl === 'string' ? photoUrl : '',
      order: 0,
    };

    return {
      success: true,
      message: response?.message || 'Success',
      data: { placeId: id, photo },
    };
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Upload thất bại',
      data: null,
    };
  }
}

/** DELETE photo */
export async function deletePhoto(placeId: string, photoId: string): Promise<ApiResponse<{ id: string } | null>> {
  const placeResponse = await getPlaceDetail(placeId);
  if (!placeResponse.success || !placeResponse.data) {
    return { success: false, message: 'Place not found', data: null };
  }

  try {
    const newPhotos = placeResponse.data.photos.filter((p) => p.id !== photoId);
    await request(`${API_BASE_URL}${CMS_API_PATH}/${placeId}`, {
      method: 'PUT',
      data: { photos: newPhotos.map((p) => p.url) },
    });
    return { success: true, message: 'Photo deleted', data: { id: photoId } };
  } catch (error: any) {
    return { success: false, message: error?.message || 'Xóa ảnh thất bại', data: null };
  }
}

/** PUT photos */
export async function updatePhotos(
  placeId: string,
  photos: PlacePhoto[],
): Promise<ApiResponse<{ photos: PlacePhoto[] } | null>> {
  try {
    await request(`${API_BASE_URL}${CMS_API_PATH}/${placeId}`, {
      method: 'PUT',
      data: { photos: photos.map((p) => p.url) },
    });
    return { success: true, message: 'Photos updated', data: { photos } };
  } catch (error: any) {
    return { success: false, message: error?.message || 'Cập nhật ảnh thất bại', data: null };
  }
}
